/**
 * The scan mode indicator: always shows which mode a scan will land in.
 *
 * Idle and Go to are a small "Scanner ready" chip; Pick and Session are a full-width coloured bar
 * naming the job, so a lab tech can see from across the bench what the next scan will do.
 */

const libPictView = require('pict-view');
const libMatrix = require('../rendering/QR-Matrix.js');

const _MODE_LABELS =
{
	Idle: 'Scanner idle',
	GoTo: 'Scan to open',
	Pick: 'Scanning into a field',
	Session: 'Scanning job in progress',
};

const _STATUS_TEXT =
{
	Found: 'Found',
	Deleted: 'Deleted',
	NotFound: 'Not recognised',
	Rejected: 'Not accepted here',
	ActionOutOfContext: 'Card not for this screen',
	Action: 'Card',
	Picked: 'Filled in',
	Unresolved: 'Read',
	Empty: 'Empty scan',
};

const _DefaultConfiguration =
{
	ViewIdentifier: 'HeadlightQR-ModeBar',
	DefaultDestinationAddress: '#HeadlightQR-ModeBar',
	AutoRender: false,
	CSS: /*css*/`
.hlqr-modebar { display: flex; align-items: center; gap: 0.75rem; padding: 0.4rem 0.9rem; border-radius: 6px; font-size: 0.9rem; background: var(--theme-color-background-panel, #f4f6f8); color: var(--theme-color-text-primary, #1f2933); border: 1px solid var(--theme-color-border-default, #d5dbe1); }
.hlqr-modebar--Pick { background: #1d4ed8; color: #fff; border-color: #1d4ed8; }
.hlqr-modebar--Session { background: #b45309; color: #fff; border-color: #b45309; font-weight: 600; font-size: 1rem; padding: 0.6rem 1rem; }
.hlqr-modebar-mode { text-transform: uppercase; letter-spacing: 0.06em; font-size: 0.75rem; font-weight: 700; }
.hlqr-modebar-label { flex: 1 1 auto; }
.hlqr-modebar-chip { font-size: 0.75rem; padding: 0.1rem 0.5rem; border-radius: 999px; background: rgba(0,0,0,0.08); white-space: nowrap; }
.hlqr-modebar-chip--ok { background: #15803d; color: #fff; }
.hlqr-modebar-chip--warn { background: #b91c1c; color: #fff; }
`,
};

class HeadlightQRModeBar extends libPictView
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, Object.assign({}, _DefaultConfiguration, pOptions), pServiceHash);
		/** @type {any} */
		this.pict;
		this.lastScan = null;
		this.unsubscribe = null;
	}

	onAfterInitialize()
	{
		const tmpProvider = this.pict.providers.HeadlightQR;
		if (tmpProvider && !this.unsubscribe)
		{
			this.unsubscribe = tmpProvider.subscribe((pType, pDetail) =>
			{
				if (pType === 'scan')
				{
					this.lastScan = pDetail;
				}
				if (pType === 'scan' || pType === 'mode')
				{
					this.paint();
				}
			});
		}
		return super.onAfterInitialize();
	}

	/**
	 * @param {string} [pDestinationAddress]
	 */
	paint(pDestinationAddress)
	{
		const tmpProvider = this.pict.providers.HeadlightQR;
		if (!tmpProvider)
		{
			return;
		}
		const tmpAddress = pDestinationAddress || this.options.DefaultDestinationAddress;
		const tmpState = tmpProvider.getScanState();
		const tmpMode = tmpState.Mode;
		const tmpLabel = tmpState.Label || _MODE_LABELS[tmpMode] || '';
		let tmpLastChip = '';
		if (this.lastScan)
		{
			const tmpGood = [ 'Found', 'Action', 'Picked' ].includes(this.lastScan.Status);
			tmpLastChip = `<span class="hlqr-modebar-chip ${tmpGood ? 'hlqr-modebar-chip--ok' : 'hlqr-modebar-chip--warn'}">Last scan: ${libMatrix.escapeXML(_STATUS_TEXT[this.lastScan.Status] || this.lastScan.Status || '')}</span>`;
		}
		const tmpWedgeChip = `<span class="hlqr-modebar-chip">${tmpState.Wedge ? 'Scanner ready' : 'Scanner off'}</span>`;
		const tmpHTML = `<div class="hlqr-modebar hlqr-modebar--${tmpMode}" role="status" aria-live="polite">`
			+ `<span class="hlqr-modebar-mode">${libMatrix.escapeXML(tmpMode === 'Session' ? 'Job' : tmpMode)}</span>`
			+ `<span class="hlqr-modebar-label">${libMatrix.escapeXML(tmpLabel)}</span>`
			+ `${tmpLastChip}${tmpWedgeChip}</div>`;
		this.pict.ContentAssignment.assignContent(tmpAddress, tmpHTML);
	}
}

module.exports = HeadlightQRModeBar;
module.exports.default_configuration = _DefaultConfiguration;
